import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { BodyText, GridBag, Gutter, Headline, InlineNotification, Link } from '@gaiads/telia-react-component-library';
import { CheckoutDelivery as CheckoutDeliveryReusable, CheckoutDeliveryTranslations, useToBreakpoint } from '@cdaas/reusable-components-daas';
import { DeliveryMethodOfOrder } from '@cdaas/reusable-components-daas/dist/src/order-api-types/api-types';
import {
  DeliveryResponse,
  DeliveryDeliveryOption,
  DeliveryProductOrder,
  DeliveryOrderLine,
  CreateProductOrder,
  CreateOrderLine,
  Currency,
} from '@cdaas/api-clients/dist/order-openapi';
import { OrganizationResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { SalesChannel, LocalProductVariant, LocalProductTypeEnum } from '@cdaas/api-clients/dist/product-service-openapi';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';

import { routes, withCustomerOrgFormPath } from '../../App/routes';
import { orderDeliveryClient, orderProductOrderClient } from '../../../api-client/order-client';
import {
  selectDeliveryDetails,
  selectOrganization,
  selectSelectedProduct,
  selectSelectedUser,
  setCompletedOrder,
  setOrderDeliveryOption,
  selectSalesChannel,
} from '../../../store/order';
import { ORDER_FLOW_CONSTANTS } from '../../../utils';
import { Loader } from '../../App/Loader';
import { MOBILE_BREAKPOINT } from '../Order';
import { isSelectedBundleAvailable } from '../OrderUtils';
import ProductCart from '../ProductCart/ProductCart';
import {
  OrderLineValues,
  MISSING_ENROLLMENT_ID_ERROR_MESSAGE,
  checkIsEnrollmentIdIsRequired,
  getCorrectBundleItemOptionValuesForAddon,
  getCorrectBundleItemOptionValuesForMainItem,
  isAllInformationAvailableForOrdering,
  isSelectedMaterial,
  isHardware,
  createOrderLine,
  createProductOrder,
} from './helpers';
import axios from 'axios';

export default function DeliveryOptions() {
  const [deliveryOption, setDeliveryOption] = useState<DeliveryResponse>();
  const [loadingDeliveryOption, setLoadingDeliveryOption] = useState<boolean>(true);
  const [loadingOrder, setLoadingOrder] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | JSX.Element | null>(null);

  const isMobile = useToBreakpoint(MOBILE_BREAKPOINT);

  const deliveryAddressStore = useSelector(selectDeliveryDetails)?.deliveryAddress;
  const deliveryContactStore = useSelector(selectDeliveryDetails)?.deliveryContact;
  const isHomeDeliverySelected = useSelector(selectDeliveryDetails)?.isHomeDelivery;
  const locationReference = useSelector(selectDeliveryDetails)?.locationReference;
  const invoicingAccount = useSelector(selectDeliveryDetails)?.invoicingAccountNumber || undefined;

  const organization: OrganizationResponse | null = useSelector(selectOrganization);

  const selectedProduct: LocalBundleEnvelopeWithChosenMainVariant | null = useSelector(selectSelectedProduct);
  const selectedUserStore = useSelector(selectSelectedUser);
  const salesChannelStore: SalesChannel | null = useSelector(selectSalesChannel);

  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('orderFlow');

  useEffect(() => {
    const timeOutHandle = setTimeout(() => {
      if (!deliveryAddressStore) {
        history.push(ORDER_FLOW_CONSTANTS.checkoutDetails);
      }
    }, 500);
    return () => clearTimeout(timeOutHandle);
    // want to run this only once at the beginning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDeliveryOptions = async () => {
    const selectedMaterials = selectedProduct?.materials?.filter(material => isSelectedMaterial(material, selectedProduct));
    const hardwareMaterials = selectedMaterials?.filter(material => isHardware(material, selectedProduct));
    // TODO: Add more informative error messages for example when product is missing material code
    if (
      !organization?.order_channel ||
      !deliveryAddressStore ||
      (!hardwareMaterials?.length && selectedProduct?.bundle.main_item.type !== LocalProductTypeEnum.Hardware)
    ) {
      setErrorMessage(t('deliveryOptions.errorDetailsIsMissing'));
      setLoadingDeliveryOption(false);
      return;
    }

    const orderLines: DeliveryOrderLine[] = hardwareMaterials?.map(material => ({ erp_material_code: material.code })) || [];

    const productOrderRequestBody: DeliveryProductOrder = {
      delivery_address: {
        zip: deliveryAddressStore?.zip || '',
        country: deliveryAddressStore?.country || '',
      },
      packages: [{ order_lines: orderLines }],
    };

    try {
      setLoadingDeliveryOption(true);
      const deliveryData: DeliveryResponse = (await orderDeliveryClient.postDelivery(organization.order_channel, productOrderRequestBody, {})).data;

      if (deliveryData.delivery_options !== undefined) {
        deliveryData.delivery_options = deliveryData.delivery_options.filter(option =>
          isHomeDeliverySelected ? option.home_delivery : option.business_parcel,
        );
      }
      setDeliveryOption(deliveryData);
    } catch (err) {
      console.error(err);
      const message = (axios.isAxiosError(err) && err.response?.data?.message) || (err instanceof Error && err.message);
      if (message) {
        setErrorMessage(message);
      } else {
        setErrorMessage(t('deliveryOptions.getDeliveryOptionsError'));
      }
    }
    setLoadingDeliveryOption(false);
  };

  useEffect(() => {
    getDeliveryOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMakeOrderClick = async (selectedDelivery: DeliveryDeliveryOption) => {
    try {
      const selectedMaterials = selectedProduct?.materials?.filter(material => isSelectedMaterial(material, selectedProduct));
      if (
        isAllInformationAvailableForOrdering(
          organization,
          selectedProduct,
          salesChannelStore,
          deliveryContactStore,
          selectedUserStore,
          deliveryAddressStore,
          selectedMaterials,
        )
      ) {
        // This enrollment check is implemented in order service.
        // Consider at some point to remove enrollment check form here so that check exists in only one place.
        // With this current implementation we are able to show meaningful enrollment error to the admin user.
        checkIsEnrollmentIdIsRequired(selectedProduct!, organization!);

        setLoadingOrder(true);
        const bundlePrice = selectedProduct!.bundle.prices![0];

        const deliveryOptionToSave: DeliveryMethodOfOrder = {
          ...selectedDelivery,
          delivery_times_valid_until: deliveryOption?.delivery_times_valid_until ?? '',
        };
        dispatch(setOrderDeliveryOption(deliveryOptionToSave));

        const orderLines: CreateOrderLine[] = selectedMaterials!.map(material => {
          let orderLineValues: OrderLineValues = {};
          const isMainProduct = material.variant_id === selectedProduct!.chosenMainItemVariantId;

          if (!isMainProduct) {
            let bundleItemOption: LocalProductVariant | undefined;
            selectedProduct!.bundle.items.every(item => {
              bundleItemOption = item.options.find(option => option.id === material.variant_id);
              if (bundleItemOption) {
                orderLineValues = getCorrectBundleItemOptionValuesForAddon(selectedProduct!, bundleItemOption, item, salesChannelStore!);
                return false;
              }
              return true;
            });
          } else {
            orderLineValues = getCorrectBundleItemOptionValuesForMainItem(selectedProduct!, salesChannelStore!, locationReference || undefined);
          }

          return createOrderLine(
            orderLineValues,
            selectedProduct,
            material,
            isMainProduct,
            salesChannelStore!,
            organization!.locale.currency.toUpperCase() as Currency,
            t('deliveryOptions.placeOrderErrorDataInvalid'),
          );
        });

        const productOrder: CreateProductOrder = createProductOrder(
          organization!,
          deliveryContactStore!,
          deliveryAddressStore!,
          isHomeDeliverySelected!,
          selectedDelivery,
          invoicingAccount,
          selectedProduct!,
          salesChannelStore!,
          bundlePrice,
          selectedUserStore ? selectedUserStore : undefined,
          orderLines,
          t('deliveryOptions.deliveryInvalid', { delivery: isHomeDeliverySelected ? 'home' : 'company' }),
        );
        const orderResponse = (await orderProductOrderClient.createProductOrder(organization!.order_channel, productOrder)).data;
        const uriParts = orderResponse.uri.split('/');
        const orderId = uriParts[uriParts.length - 1];
        const order = (await orderProductOrderClient.getProductOrder(organization!.order_channel, orderId)).data;
        dispatch(setCompletedOrder(order));
        history.push(ORDER_FLOW_CONSTANTS.orderCompleted);
      } else {
        throw new Error(t('deliveryOptions.placeOrderErrorDataInvalid'));
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.data?.message) {
        setErrorMessage(err.response.data.message);
      } else if (err instanceof Error && err.message === MISSING_ENROLLMENT_ID_ERROR_MESSAGE && organization?.id) {
        setErrorMessage(
          <BodyText>
            <Trans
              t={t}
              i18nKey="deliveryOptions.enrollmentError"
              components={{
                a: <Link to={withCustomerOrgFormPath(routes.companyInformation, organization.id)} />,
              }}
            />
          </BodyText>,
        );
      } else {
        setErrorMessage((err instanceof Error && err.message) || t('deliveryOptions.placeOrderError'));
      }
    }
    setLoadingOrder(false);
  };

  const handleBackClick = () => history.push(ORDER_FLOW_CONSTANTS.checkoutDetails);

  const translations: CheckoutDeliveryTranslations = {
    title: t('deliveryOptions.title'),
    deliveryEstimateNotAvailable: t('deliveryOptions.deliveryEstimateNotAvailable'),
    optionDelivered: t('deliveryOptions.optionDelivered'),
    optionDescription: t('deliveryOptions.optionDescription'),
    deliveryText: t('deliveryOptions.deliveryText'),
    deliveryComment: t(selectedUserStore ? 'deliveryOptions.deliveryComment' : 'deliveryOptions.deliveryCommentForOrganization'),
    confirmOrderCheckboxLabel: t('deliveryOptions.confirmOrderCheckboxLabel'),
    orderButton: t('deliveryOptions.orderButton'),
    cancelButton: t('deliveryOptions.cancelButton'),
    outOfStockDeliveryComment: t('deliveryOptions.outOfStockDeliveryComment'),
  };

  return (
    <>
      <Gutter size={'layout-5'} />
      <Headline size={'lg'}>{t('deliveryOptions.headline')}</Headline>
      <Gutter size={'layout-5'} />

      <GridBag showGridLines noGutter>
        <GridBag.Item md={12} lg={6}>
          {errorMessage && (
            <InlineNotification title={'Error'} variant={'error'} margin={{ bottom: 'md' }}>
              {errorMessage}
            </InlineNotification>
          )}
          {loadingDeliveryOption && <Loader />}
          {!loadingDeliveryOption && !!deliveryAddressStore && !!deliveryOption && (
            <CheckoutDeliveryReusable
              translations={translations}
              handleBackClick={handleBackClick}
              handleNextClick={handleMakeOrderClick}
              selectedAddress={deliveryAddressStore}
              deliveryOptionsData={deliveryOption}
              nextButtonClickedSpinner={loadingOrder}
              outOfStockDelivery={!isSelectedBundleAvailable(selectedProduct, selectedProduct?.chosenMainItemVariantId)}
              ProductCart={isMobile ? <ProductCart isMobile /> : undefined}
            />
          )}
        </GridBag.Item>
        {!isMobile ? (
          <GridBag.Item lg={6} className={'order-product-cart-container'}>
            <GridBag>
              <GridBag.Item lg={4}></GridBag.Item>
              <GridBag.Item lg={8} className={'order-product-cart-border'}>
                <ProductCart />
              </GridBag.Item>
            </GridBag>
          </GridBag.Item>
        ) : undefined}
      </GridBag>
    </>
  );
}
